/**
 * Families Tab
 * Admin interface for viewing and managing all households
 */

import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import type React from "react"
import { useMemo, useState } from "react"
import { FiSearch } from "react-icons/fi"

import { HouseholdActionsMenu } from "@/components/layout/Common/HouseholdActionsMenu"
import {
  DataTable,
  type DataTableColumn,
} from "@/components/organisms/DataTable"
import { AddHousehold } from "@/features/households"
import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  HouseholdListResponse,
  HouseholdResponse,
} from "@/types/household"
import { unwrapStandardResponse } from "@/utils/apiResponse"

const PER_PAGE = 25
type SortField = "created_at" | "updated_at"
type SortDirection = "asc" | "desc"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

function getHouseholdsQueryOptions({
  page,
  search,
  sort,
  direction,
}: {
  page: number
  search?: string
  sort: SortField
  direction: SortDirection
}) {
  return {
    queryFn: async () => {
      const response = await api.households.readHouseholdsApiV1HouseholdsGet({
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        search,
        sortBy: sort,
        sortOrder: direction,
      })

      const payload =
        unwrapStandardResponse<HouseholdListResponse>(response)
      const items = payload?.items ?? []
      const total = payload?.total ?? 0

      return {
        data: items,
        count: total,
      }
    },
    queryKey: [
      "households",
      {
        page,
        search,
        sort,
        direction,
      },
    ],
  }
}

export function HouseholdsManagementTab() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState<string>()
  const [searchInput, setSearchInput] = useState("")
  const [sort, setSort] = useState<SortField>("created_at")
  const [direction, setDirection] = useState<SortDirection>("desc")
  const { hasPermission } = usePermissions()

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getHouseholdsQueryOptions({
      page,
      search,
      sort,
      direction,
    }),
    placeholderData: (previous) => previous,
  })

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextValue = searchInput.trim()
    setSearch(nextValue || undefined)
    setPage(1)
  }

  const handleSortChange = (nextSort: SortField) => {
    setSort(nextSort)
    setPage(1)
  }

  const handleDirectionChange = (nextDirection: SortDirection) => {
    setDirection(nextDirection)
    setPage(1)
  }

  const records = data?.data?.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const showActions =
    hasPermission("households:update") || hasPermission("households:delete")

  const columns = useMemo<DataTableColumn<HouseholdResponse>[]>(() => {
    const tableColumns: DataTableColumn<HouseholdResponse>[] = [
      {
        key: "id",
        label: "ID",
        render: (household) => <Text truncate>{household.id}</Text>,
      },
      {
        key: "parent_first_name",
        label: "Parent First Name",
        render: (household) => (
          <Text>{formatFieldValue(household.parent_first_name, "str")}</Text>
        ),
      },
      {
        key: "parent_last_name",
        label: "Parent Last Name",
        render: (household) => (
          <Text>{formatFieldValue(household.parent_last_name, "str")}</Text>
        ),
      },
      {
        key: "email",
        label: "Email",
        render: (household) => (
          <Text>{formatFieldValue(household.email, "str")}</Text>
        ),
      },
      {
        key: "phone",
        label: "Phone",
        render: (household) => (
          <Text>{formatFieldValue(household.phone, "str")}</Text>
        ),
      },
      {
        key: "address",
        label: "Address",
        render: (household) => (
          <Text>{formatFieldValue(household.address, "str")}</Text>
        ),
      },
      {
        key: "is_active",
        label: "Active",
        render: (household) => (
          <Text>{formatFieldValue(household.is_active, "bool")}</Text>
        ),
      },
    ]

    if (showActions) {
      tableColumns.push({
        key: "actions",
        label: "Actions",
        render: (household) => <HouseholdActionsMenu household={household} />,
      })
    }

    return tableColumns
  }, [showActions])

  if (isLoading) {
    return (
      <Box>
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap={4}
          minH="240px"
        >
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading households...</Text>
        </Flex>
      </Box>
    )
  }

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="lg">Families</Heading>
        <HStack gap={4} alignItems="center">
          <form onSubmit={handleSearchSubmit}>
            <HStack gap={2}>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search households"
                size="sm"
                width="220px"
                aria-label="Search households"
              />
              <Button type="submit" size="sm" colorPalette="blue">
                Search
              </Button>
            </HStack>
          </form>
          <Box
            {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
            as="select"
            size="sm"
            value={sort}
            onChange={(event) =>
              handleSortChange(
                (event.target as HTMLSelectElement).value as SortField,
              )
            }
            aria-label="Sort households"
            width="170px"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.200"
            px={3}
            py={1}
            fontSize="sm"
            bg="white"
            _hover={{ borderColor: "gray.300" }}
            _focus={{
              borderColor: "blue.500",
              outline: "none",
              boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
            }}
          >
            <option value="created_at">Newest</option>
            <option value="updated_at">Recently updated</option>
          </Box>
          <Box
            {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
            as="select"
            size="sm"
            value={direction}
            onChange={(event) =>
              handleDirectionChange(
                (event.target as HTMLSelectElement).value as SortDirection,
              )
            }
            aria-label="Sort direction"
            width="150px"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.200"
            px={3}
            py={1}
            fontSize="sm"
            bg="white"
            _hover={{ borderColor: "gray.300" }}
            _focus={{
              borderColor: "blue.500",
              outline: "none",
              boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
            }}
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </Box>
          <AddHousehold />
        </HStack>
      </Flex>

      {records.length === 0 ? (
        <VStack gap={4} py={12} alignItems="center">
          <FiSearch size={48} color="gray" />
          <VStack textAlign="center" gap={2}>
            <Heading size="md" color="gray.600">
              No households found
            </Heading>
            <Text color="gray">Add a new household to get started</Text>
          </VStack>
        </VStack>
      ) : (
        <>
          <Box opacity={isPlaceholderData ? 0.5 : 1}>
            <DataTable columns={columns} data={records} size="sm" />
          </Box>
          <Flex justifyContent="flex-end" mt={4} gap={2}>
            <Button
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled>
              Page {page} of {Math.ceil(count / PER_PAGE)}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setPage(Math.min(Math.ceil(count / PER_PAGE), page + 1))
              }
              disabled={page >= Math.ceil(count / PER_PAGE)}
            >
              Next
            </Button>
          </Flex>
        </>
      )}
    </Box>
  )
}
